import '../collection-action-menu/collection-action-menu.js';
import '../wishlist-collection-head/wishlist-collection-head.js';
import {customElement, property, queryAssignedElements, state} from 'lit/decorators.js';
import {ifDefined} from 'lit/directives/if-defined.js';
import {css, CSSResult, html, LitElement, nothing, PropertyValues, TemplateResult, unsafeCSS,} from 'lit';
import arrowBackIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-16_arrow-left.svg';
import Close from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-24_close.svg';
import heartIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/f-ic-24_heart.svg';
import {consume, provide} from '@lit/context';
import {
  CollectionTrackingContext,
  Curator,
  Messages,
  Product,
  ProductIdentifierContainer,
  SizeDeterminedContainer,
  SizeSelectionContainer,
  SwitchModelContainer,
  WishlistCollection,
  WishlistMailProductAdded,
  WishlistMutationEvent
} from '../../../@types/wishlist.js';
import '../wishlist-product/wishlist-product.js';
import '../product-family/product-family-slider.js';
import '../filter/filter.js';
import '../filter/filter-button.js';
import '../event-icon/event-icon.js';
import '../product-action-menu/product-action-menu.js';
import '../mail-product-added/mail-product-added.js';
import '../size-layer/size-layer.js';
import '../add-to-list-layer/add-to-list-layer.js';
import '../options/options.js';
import styles from './wishlist-container.scss';
import grids from '../../../styles/grids.scss';
import typographyStyles from '../../../styles/responsive-nui-typography.scss';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import {
  trackPageView,
  trackViewCollections,
  trackViewCuratedCollections,
  trackViewWishlist
} from '../../../scripts/service/cube-tracking-layer.js';
import {FilterMenu} from '../../../scripts/filter/filter-menu.js';
import {filterContext} from '../../../scripts/context/filterContext.js';
import {HeadRenderCase, ProductsRenderCase, WishlistRenderCase} from '../../../scripts/service/wishlist-render-case.js';
import {OptionUIModel} from '../../../scripts/filter/option-ui-model.js';
import {previewAnimationContext, PreviewState} from '../../../scripts/context/previewAnimationContext.js';
import {sizeLayerContext, SizeLayerState} from '../../../scripts/context/sizeLayerContext.js';
import {productFamilyContext, ProductFamilyState} from '../../../scripts/context/productFamilyContext.js';
import {WishlistAppConfig, wishlistAppConfigContext} from '../../../scripts/context/appConfigContext.js';
import {
  collectionsContext,
  curatedCollectionContext,
  productsContext
} from '../../../scripts/context/wishlistContext.js';
import {ProductIdentifier} from '../../../scripts/service/product-identifier.js';
import {CollectionsState, CuratedCollectionsState, ProductsState} from '../../../scripts/context/wishlist-state.js';
import {addToListLayerContext, AddToListLayerState} from '../../../scripts/context/addToListLayerContext.js';
import {config} from '../../../scripts/service/config.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {addTitleToIcon, hideIconFromAssistiveTechnology} from '../icon/icon-accessibility.js';
import {loadFromDOM} from '../../../scripts/utils/load-from-dom.js';
import {repeat} from 'lit/directives/repeat.js';
import {mediaQuery} from '../../../scripts/service/media-query.js';
import {classMap} from 'lit/directives/class-map.js';
import type {WishlistProduct} from '../wishlist-product/wishlist-product.js';
import {curatorLayerContext, CuratorLayerState} from '../../../scripts/context/curatorLayerContext.js';

@customElement('ents-wishlist-container')
export class WishlistContainer extends LitElement {
  @property({type: Object, attribute: 'collection'})
  private wishlistCollection?: WishlistCollection;

  @property({type: String, attribute: 'curated-collection'})
  private curatedCollectionId?: string;

  @property({type: Boolean, attribute: 'logged-in'})
  private loggedIn: boolean = false;

  @property({type: Object, attribute: false})
  private curator?: Curator;

  @consume({context: i18nContext})
  private messages!: Messages;

  @consume({context: wishlistAppConfigContext})
  private wishlistAppConfig!: WishlistAppConfig;

  @consume({context: curatedCollectionContext})
  private curatedCollectionsState!: CuratedCollectionsState;

  @consume({context: collectionsContext, subscribe: true})
  private collectionsState!: CollectionsState;

  @consume({context: productsContext, subscribe: true})
  @property({attribute: false})
  private productsState!: ProductsState;

  @consume({context: curatorLayerContext, subscribe: true})
  @property({attribute: false})
  private curatorLayerState!: CuratorLayerState;

  @provide({context: filterContext})
  @property({attribute: false})
  private filterMenu!: FilterMenu;

  @provide({context: previewAnimationContext})
  private previewAnimationContext: PreviewState = {
    previewAnimationStarted: false,
    previewAnimationEnded: false,
    startPreviewAnimation: () => {
      this.previewAnimationContext = {...this.previewAnimationContext, previewAnimationStarted: true};
    },
    endPreviewAnimation: () => {
      this.previewAnimationContext = {...this.previewAnimationContext, previewAnimationEnded: true};
    },
  };

  @provide({context: sizeLayerContext})
  private sizeLayerState: SizeLayerState = {
    product: null,
    selectedArticle: null,
    trackingContext: '',
    triggerA2C: false,
    open: false,
    update: (newState) => {
      this.sizeLayerState = {
        ...this.sizeLayerState, ...newState,
      };
    },
    backButtonCallback: undefined,
  };

  @provide({context: productFamilyContext})
  private productFamilyState: ProductFamilyState = {
    sliderData: null,
    open: false,
    beauty: false,
    update: (newState) => {
      this.productFamilyState = {
        ...this.productFamilyState, ...newState,
      };
    },
    backButtonCallback: undefined,
  };

  @provide({context: addToListLayerContext})
  private addToListLayerState: AddToListLayerState = {
    open: false,
    update: (newState) => {
      this.addToListLayerState = {
        ...this.addToListLayerState, ...newState,
      };
    },
    backButtonCallback: undefined,
  };

  @state()
  private modelViewEnabled: boolean = false;

  @queryAssignedElements({slot: 'inlist-newsletter'})
  private inlistNewsletterSlotElements!: HTMLElement[];

  @state()
  private products: Product[] = [];

  @state()
  private wishlistMailProductAdded: WishlistMailProductAdded | null = null;

  @state()
  private filteredProducts: Product[] = [];

  private updatePending = false;

  private scheduleLoadCampFlagsTimeout?: number;

  private renderIdleTimeout?: number;

  private renderTimeout?: number;

  private scrollAbortController: AbortController | null = null;

  private productsViewportObserver: IntersectionObserver | null = null;

  private productsTrackingObserver: IntersectionObserver | null = null;

  private productsPromotionObserver: MutationObserver | null = null;

  private processedPromotionElements = new WeakSet<Element>();

  private readonly SURVEY_VERSION = 'v2';

  private async setProductsForCollection() {
    if (this.curatedCollectionId) {
      const curatedCollection =
        await this.curatedCollectionsState.getOne(this.wishlistAppConfig.curatedCollectionApiEndpoint, this.curatedCollectionId);

      // user opened link to curated collection that does not include their BOCA
      // redirect to standard shared collection link
      if (!curatedCollection) {
        const newUrl = window.location.href.replace(/\/cl\//, '/l/');
        window.location.replace(newUrl);
        return;
      }

      this.products = curatedCollection.products;
      this.wishlistCollection = curatedCollection.collection;
      this.curator = curatedCollection.curator;
      this.curatorLayerState.update({curator: this.curator});
    } else {
      this.products = this.productsState.getProductsToRender(this.wishlistCollection?.id);
    }
  };

  static get styles(): CSSResult[] {
    return [css`${unsafeCSS(styles)}`, css`${unsafeCSS(typographyStyles)}`, css`${unsafeCSS(grids)}`];
  }

  connectedCallback() {
    super.connectedCallback();

    // due to progressive rendering and CSS "content-visibility: auto" on wishlist products, loading the page with a
    // non-zero scroll position does not work correctly, so we always reset the scroll position to the top when loading the page
    window.scrollTo(0, 0);

    this.setProductsForCollection();

    document.addEventListener('ents:wishlist:removed', this.handleProductRemoved);
    document.addEventListener('ents:wishlist:added', this.handleProductAdded);
    document.addEventListener('ents:article:addtocart', this.handleAddToCart);
    document.addEventListener('ents:size-layer-item:selected', this.handleSizeSelected);
    document.addEventListener('ents:cta-event-emitted', this.handleCtaReset);

    this.createAndSetFilterMenu();
    window.addEventListener('popstate', () => this.createAndSetFilterMenu());
    document.addEventListener('ents:switch-model-view', this.switchModelView);
    document.addEventListener('camp:aktionsflags:loader:ready', () => {
      this.triggerCampEvent();
    }, {once: true});
    document.addEventListener('ents:wishlist-scroll-to-product', this.scrollToProduct);
    mediaQuery.on('change:width', this.handleResize);

    this.modelViewEnabled = this.loadModelViewFromLocalStorage();
    this.wishlistMailProductAdded = loadFromDOM('#ents-wishlist-mail-product-added-data');
    setTimeout(() => this.trackPageEvents());
    this.setupProductObservers();
  }

  private setupProductObservers() {
    this.productsViewportObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          (entry.target as WishlistProduct).setInViewportDebounced(entry.isIntersecting);
        });
      },
      {rootMargin: '2000px'}
    );

    this.productsTrackingObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          (entry.target as WishlistProduct).trackViewDebounced(entry.isIntersecting);
        });
      },
      {threshold: 0.5}
    );

    this.productsPromotionObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'attributes') continue;

        const target = mutation.target as HTMLElement;

        if (this.processedPromotionElements.has(target)) continue;

        const containsFlag = target.getAttribute('contains-flag');
        if (!containsFlag) continue;

        this.processedPromotionElements.add(target);

        const product = this.getProductForElement(target);
        if (product && containsFlag === 'true') {
          const artikelId = target.getAttribute('artikel-id');
          product.handlePromotionDetected(artikelId);
        }
      }
    });
  }

  private getProductForElement(element: HTMLElement): WishlistProduct | null {
    const root = element.getRootNode();
    if (root instanceof ShadowRoot) {
      return root.host as WishlistProduct;
    }
    return null;
  }

  private trackPageEvents() {
    const trackingType = this.determineTrackingType();
    trackPageView(trackingType.page, this.products.length === 0, this.loggedIn);
    trackViewWishlist(trackingType.context, this.products);
    if (this.curatedCollectionId && this.wishlistCollection) {
      trackViewCuratedCollections(trackingType.context, [{
        collection: this.wishlistCollection,
        quantity: this.products.length,
      }]);
      return;
    }
    trackViewCollections(trackingType.context, this.collectionsState.entities, this.productsState);
  }

  private determineTrackingType(): { page: string, context: string } {
    if (this.curatedCollectionId) {
      return {page: 'curated_collection_detail', context: 'curated_collection'};
    } else if (this.productsState.isShared()) {
      return {page: 'shared_collection_detail', context: 'collection'};
    } else if (this.wishlistCollection) {
      return {page: 'collection_detail', context: 'collection'};
    } else {
      return {page: 'all_articles', context: 'wishlist'};
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('ents:wishlist:removed', this.handleProductRemoved);
    document.removeEventListener('ents:wishlist:added', this.handleProductAdded);
    document.removeEventListener('ents:article:addtocart', this.handleAddToCart);
    document.removeEventListener('ents:size-layer-item:selected', this.handleSizeSelected);
    document.removeEventListener('ents:cta-event-emitted', this.handleCtaReset);

    window.removeEventListener('popstate', this.createAndSetFilterMenu);
    document.removeEventListener('ents:switch-model-view', this.switchModelView);
    document.removeEventListener('ents:wishlist-scroll-to-product', this.scrollToProduct);
    mediaQuery.off('change:width', this.handleResize);

    if (this.scheduleLoadCampFlagsTimeout) {
      clearTimeout(this.scheduleLoadCampFlagsTimeout);
    }
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
    }
    if (this.renderIdleTimeout && 'cancelIdleCallback' in window) {
      cancelIdleCallback(this.renderIdleTimeout);
    }
    if (this.scrollAbortController) {
      this.scrollAbortController.abort();
      this.scrollAbortController = null;
    }
    this.productsViewportObserver?.disconnect();
    this.productsTrackingObserver?.disconnect();
    this.productsPromotionObserver?.disconnect();
  }

  private getProductById(id: string): WishlistProduct | null {
    return this.shadowRoot?.getElementById(`ents-wishlist-product-${id}`) as WishlistProduct;
  }

  private handleProductRemoved = (e: WishlistMutationEvent) => {
    const productEl = this.getProductById(e.detail.identifier);
    if (productEl) {
      productEl.animateRemoval();
    }
  };

  private handleProductAdded = (e: WishlistMutationEvent) => {
    const productEl = this.getProductById(e.detail.identifier);
    if (productEl) {
      productEl.animateAddition();
    }
  };

  private handleSizeSelected = (e: CustomEvent<SizeSelectionContainer>) => {
    const productEl = this.getProductById(e.detail.identifier);
    if (productEl) {
      productEl.handleSizeSelection(e.detail);
    }
  };

  private handleCtaReset = (e: CustomEvent<ProductIdentifierContainer>) => {
    const productEl = this.getProductById(e.detail.identifier);
    if (productEl) {
      productEl.resetTriggers();
    }
  };

  private handleAddToCart = (e: CustomEvent<SizeDeterminedContainer>) => {
    // AddToCart uses 'syan' (style ID) rather than the unique ProductIdentifier
    // We query specifically for products with this SYAN attribute
    // Note: We use the data-test attribute because it contains the syan in your render method
    const products = this.shadowRoot?.querySelectorAll(`ents-wishlist-product[data-test-ents-wishlist-product="${e.detail.syan}"]`);

    products?.forEach((el) => {
      (el as WishlistProduct).handleAddToCart(e.detail);
    });
  };

  private handleResize = () => {
    this.requestUpdate();
  };

  private handleScheduleLoadCampFlags() {
    if (this.scheduleLoadCampFlagsTimeout) {
      clearTimeout(this.scheduleLoadCampFlagsTimeout);
    }
    this.scheduleLoadCampFlagsTimeout = window.setTimeout(() => {
      // debounce CAMP event to wait for all newly rendered products with a <camp-aktionsflag> element
      this.triggerCampEvent();
      this.scheduleLoadCampFlagsTimeout = undefined;
    }, 150);
  }

  private createAndSetFilterMenu() {
    this.filterMenu = FilterMenu.createMenuFor(this.products);

    // if the promotion filter option is active here, it means the page was loaded with the filter in the URL.
    // remove it if there are 0 matches, because this means that there are no articles with `isInPromotion`
    // set by our backend and so filtering by promotion will not work until the CAMP API call has happened.
    const promotionFilterActive = this.filterMenu.filter.flatMap(f => f.getActiveOptions(false)).some(o => o.id === 'in-promotion');
    if (promotionFilterActive && this.filterMenu.resultCount === 0) {
      this.filterMenu = this.filterMenu.toggleFilterOption(this.products, 'in-promotion', true);
      this.filterMenu = this.filterMenu.applyEditState();
    }
  }

  private onWishlistFilterChangedEvent = (e: CustomEvent<{ optionId: string, allowRemove: boolean }>) => {
    this.filterMenu = this.filterMenu.toggleFilterOption(this.products, e.detail.optionId, e.detail.allowRemove);
  };

  private onWishlistFilterOpen() {
    FilterMenu.invalidateCache();
    this.filterMenu = this.filterMenu.open(this.products);
  }

  private onWishlistFilterClose() {
    this.filterMenu = this.filterMenu.close();
  }

  private onWishlistFilterApply = () => {
    this.filterMenu = this.filterMenu.applyEditState();
  };

  private onWishlistFilterRevert = () => {
    this.filterMenu = this.filterMenu.undoEditState(this.products);
  };

  private onWishlistFilterReset = () => {
    this.filterMenu = this.filterMenu.resetAllFilters(this.products);
  };

  private onWishlistFilterResetFilter = (e: CustomEvent<{ filterId: string }>) => {
    this.filterMenu = this.filterMenu.resetFilter(this.products, e.detail.filterId);
  };

  private onScrollTop = () => {
    setTimeout(() => {
      const containerTop = this.getBoundingClientRect().top;
      if (containerTop < 0) {
        this.scrollIntoView({behavior: 'instant', block: 'start'});
      }
    }, 50);
  };

  protected renderFilterButton() {
    return html`
      <div class="ents-wishlist-filter-button">
        <ents-filter-button
          @apply="${this.onWishlistFilterApply}"
          @changed="${this.onWishlistFilterChangedEvent}"
          @open="${this.onWishlistFilterOpen}"
          @scroll-top="${this.onScrollTop}"
        ></ents-filter-button>
      </div>
    `;
  }

  private reapplyFilter() {
    this.filterMenu = this.filterMenu.reapplyFilter(this.products);
  }

  private resetAllFilters() {
    this.filterMenu = this.filterMenu.resetAllFilters(this.products);
  }

  private switchModelView = (e: CustomEvent<SwitchModelContainer>) => {
    this.modelViewEnabled = e.detail.modelViewEnabled;
    window.localStorage.setItem('ents-wishlist-model-view', String(this.modelViewEnabled));
  };

  private loadModelViewFromLocalStorage(): boolean {
    return window.localStorage.getItem('ents-wishlist-model-view') === 'true';
  }

  private renderEmptyCollection() {
    if (this.productsState.entities.length > 0) {
      return html`
        <div data-test-ents-wishlist-collection-empty>
          ${this.messages['ents_wishlist_collection_details_empty']}
        </div>
      `;
    } else {
      return html`
        <div data-test-ents-wishlist-collection-empty-favorites class="ents-wishlist-collection-empty-favorites">
          ${unsafeHTML(this.messages['ents_wishlist_collection_details_emptyFavorites'])}
          <div class="ents-wishlist-collection-empty-favorites__button-container">
            <a href="${config.homepage}">
              <button class="bdk-button" type="button" variant="outline" size="medium">
                <span
                  class="ents-heading ents-text-uppercase">${this.messages['ents_wishlist_collection_details_emptyFavorites_cta']}</span>
              </button>
            </a>
          </div>
        </div>
      `;
    }
  }

  private renderOverfilteredWishlist() {
    const chipsFromAllFiltersSorted = this.filterMenu.filter
      .filter(filter => !filter.isSort)
      .flatMap(filter => filter.getActiveOptions(false))
      .sort((optionA, optionB) => optionA.label(this.messages).localeCompare(optionB.label(this.messages)));
    return html`
      <ents-options class="ents-options-container">
        ${this.renderFilterButton()}
      </ents-options>
      <div class="ents-wishlist-overfiltered"
           @changed="${this.handleOverfilteredChange}">
        <h3 class="ents-copy-medium-bold" data-ents-wishlist-overfiltered-message>
          ${this.messages['ents_wishlist_overfiltered_message']}</h3>
        ${chipsFromAllFiltersSorted.map(option => (this.renderChip(option)))}
        <div>
          <button class="ents-wishlist-overfiltered__reset-all-button bdk-link-primary" size="small"
                  @click="${this.onWishlistFilterReset}"
                  data-test-ents-overfiltered-reset-all-button>
            ${this.messages['ents_wishlist_filter_reset_all']}
          </button>
        </div>
      </div>
    `;
  }

  protected renderWishlist(products: Product[]) {
    return html`
      ${this.renderWishlistGrid(products)}
      <ents-filter
        @apply="${this.onWishlistFilterApply}"
        @revert="${this.onWishlistFilterRevert}"
        @changed="${this.onWishlistFilterChangedEvent}"
        @reset="${this.onWishlistFilterReset}"
        @reset-filter="${this.onWishlistFilterResetFilter}"
        @close="${this.onWishlistFilterClose}"
        @scroll-top="${this.onScrollTop}"
      ></ents-filter>
      <ents-product-action-menu
        data-test-ents-action-menu="product-action-menu-layer"
        ?curated-collection="${!!this.curatedCollectionId}"
      >
        <div slot="product-banner">
          <slot name="product-banner"></slot>
        </div>
        <div slot="gwp-banner">
          <slot name="gwp-banner"></slot>
        </div>
      </ents-product-action-menu>
      <ents-size-layer ?logged-in="${this.loggedIn}"></ents-size-layer>
      <ents-product-family-slider></ents-product-family-slider>
      <ents-add-to-list-layer></ents-add-to-list-layer>
    `;
  }

  private renderEventsIcon() {
    if (this.wishlistCollection || this.productsState.isShared()) {
      return nothing;
    }
    return html`
      <ents-event-icon
        ?logged-in="${this.loggedIn}"
      ></ents-event-icon>
    `;
  }

  private renderMailProductAdded() {
    const productAddedOrDuplicate = !!(this.wishlistMailProductAdded && (this.wishlistMailProductAdded.added || this.wishlistMailProductAdded.duplicate));
    return html`
      <ents-wishlist-mail-product-added
        syan="${ifDefined(productAddedOrDuplicate ? this.wishlistMailProductAdded!.syan : undefined)}"
        color-id="${ifDefined(productAddedOrDuplicate ? this.wishlistMailProductAdded!.colorId : undefined)}"
        duplicate="${ifDefined(productAddedOrDuplicate ? this.wishlistMailProductAdded!.duplicate : undefined)}"
        added-message="${ifDefined(productAddedOrDuplicate ? this.messages['ents_wishlist_snackbar_added'] : undefined)}"
        duplicate-message="${ifDefined(productAddedOrDuplicate ? this.messages['ents_wishlist_snackbar_added_duplicate'] : undefined)}"
        preview-image="${ifDefined(productAddedOrDuplicate ? this.wishlistMailProductAdded!.productImage : undefined)}"
        description-text="${ifDefined(productAddedOrDuplicate ? this.wishlistMailProductAdded!.descriptionText : undefined)}">
        ${!this.loggedIn ? html`
          <div class="ents-wishlist-hint-box" data-test-ents-mail-product-added-hint>
            <h3 class="ents-copy-bold">${this.messages['ents_wishlist_mail_product_added_headline']}</h3>
            <p class="ents-copy">
              ${this.messages['ents_wishlist_mail_product_added_text']}
              <br/>
              <br/>
              <a
                class="bdk-button" type="button" variant="outline" size="medium"
                href="${this.wishlistAppConfig.loginEndpointWithFavoritesReferrer}"
              >
                ${this.messages['ents_wishlist_empty_logintext']}
              </a>
            </p>
          </div>
        ` : nothing}
      </ents-wishlist-mail-product-added>
    `;
  }

  // Public method called programmatically from executeOnOriginPage
  public scrollToProduct = (e: CustomEvent<ProductIdentifierContainer>) => {
    const elementId = `ents-wishlist-product-${e.detail.identifier}`;
    let element = this.shadowRoot?.getElementById(elementId) as WishlistProduct;
    if (!element) {
      const product = this.products.find(p =>
        ProductIdentifier.createByProduct(p).id === e.detail.identifier
      );
      if (product && !this.filteredProducts.includes(product)) {
        this.resetAllFilters();
      }
    }

    // wait for update after potentially resetting filters and updating "renderedCount"
    this.updateComplete.then(() => {
      element = this.shadowRoot?.getElementById(elementId) as WishlistProduct;
      if (element) {
        element.forceFullRendering();

        requestAnimationFrame(() => {
          element.scrollIntoView({behavior: 'smooth', block: 'center'});
          this.onScrollComplete(() => {
            // scroll again instantaneously to correct the scroll position, which may be incorrect due
            // to progressive rendering and CSS "content-visibility: auto" on wishlist products
            element.scrollIntoView({behavior: 'instant', block: 'center'});
            element.shadowRoot?.querySelector('a')?.focus({preventScroll: true});
          });
        });
      }
    });
  };

  private onScrollComplete(callback: () => void): void {
    if (this.scrollAbortController) {
      this.scrollAbortController.abort();
    }
    this.scrollAbortController = new AbortController();
    const signal = this.scrollAbortController.signal;

    if ('onscrollend' in (window as unknown as object)) {
      const scrollEndHandler = () => {
        if (signal.aborted) return;
        callback();
      };

      window.addEventListener('scrollend', scrollEndHandler, {once: true, signal});
    } else {
      // Polyfill Logic
      let lastScrollY = window.scrollY;
      let stableCount = 0;
      const requiredStableFrames = 3;

      const checkScrollComplete = () => {
        if (signal.aborted) return;

        if (window.scrollY === lastScrollY) {
          stableCount++;
          if (stableCount >= requiredStableFrames) {
            callback();
            return;
          }
        } else {
          stableCount = 0;
          lastScrollY = window.scrollY;
        }
        requestAnimationFrame(checkScrollComplete);
      };

      requestAnimationFrame(checkScrollComplete);
    }
  }

  private renderWishlistGrid(products: Product[]): TemplateResult {
    if (!this.updatePending) {
      this.updatePending = true;

      if ('requestIdleCallback' in (window as unknown as object)) {
        this.renderIdleTimeout = requestIdleCallback(() => {
          if (!this.isConnected) return;
          this.updatePending = false;
        }, {timeout: 100});
      } else {
        this.renderTimeout = window.setTimeout(() => {
          if (!this.isConnected) return;
          this.updatePending = false;
        }, 16);
      }
    }

    const gridClass = this.wishlistAppConfig.productTileRedesign
      ? 'ents-wishlist-grid ents-wishlist-grid--redesign'
      : 'ents-wishlist-grid';

    const surveyHeadingClasses = this.wishlistAppConfig.productTileRedesign ? 'ents-heading ents-s:heading-small ents-m:heading' : 'ents-heading';
    const surveyTextClasses = this.wishlistAppConfig.productTileRedesign ? 'ents-copy-small ents-m:copy' : '';
    const surveyTitle = 'Wie verwalten Sie Ihre Favoriten am liebsten?';
    const surveyDescription = 'Ihre Meinung zählt: Helfen Sie uns, Ihren Favoritenbereich zu verbessern.';

    return html`
      <ents-options class="ents-options-container">
        ${this.renderFilterButton()}
      </ents-options>
      <div class="${gridClass}">
        ${repeat(
          products,
          product => ProductIdentifier.createByProduct(product).id,
          (product, index) => this.renderSingleProduct(product, index, products.length),
        )}
        <div class="ents-inlist-survey" ?hidden="${!this.showSurvey()}">
          <button
            @click="${this.hideSurvey}"
            class="closeButton bdk-tw-button-icon"
            variant="just-icon"
            size="small"
            type="button"
            data-test-survey-close-button
            aria-label="Close"
          >
            <span role="presentation"> ${hideIconFromAssistiveTechnology(Close())} </span>
          </button>

          <div class="content">
            <div class="text">
              <span class="heart">${addTitleToIcon(heartIcon(), surveyTitle)}</span>
              <h2 class="${surveyHeadingClasses}">${surveyTitle}</h2>
              <p class="${surveyTextClasses}">${surveyDescription}</p>
              <button @click="${this.surveyOpen}" class="bdk-button surveyButton" type="button" variant="outline"
                      size="small">
                <span>Umfrage starten</span>
              </button>
            </div>
          </div>
        </div>
        <div class="ents-inlist-newsletter" ?hidden="${!this.hasInlistNewsletterContent()}">
          <slot name="inlist-newsletter"></slot>
        </div>
      </div>
    `;
  }

  private hasInlistNewsletterContent(): boolean {
    const firstSlotElement = this.inlistNewsletterSlotElements[0]?.querySelector('slot');
    if (firstSlotElement instanceof HTMLSlotElement) {
      const firstRealSlottedElement = firstSlotElement.assignedElements()[0];
      if (!firstRealSlottedElement || firstRealSlottedElement?.getBoundingClientRect()?.height < 2) {
        return false;
      }
    }
    return true;
  }

  private showSurvey(): boolean {
    return this.wishlistAppConfig.surveyEnabled && !document.cookie.includes(`ents_wishlistSurvey=${this.SURVEY_VERSION}`);
  }

  private renderSingleProduct(product: Product, index: number, countListItems: number): TemplateResult {
    return html`
      <ents-wishlist-product
        id="ents-wishlist-product-${ProductIdentifier.createByProduct(product).id}"
        class="ents-wishlist-product${classMap({'ents-wishlist-product--redesign': this.wishlistAppConfig.productTileRedesign})}"
        selected-article-id="${product.articleId}"
        position="${index + 1}"
        ?model-view-enabled="${this.modelViewEnabled}"
        .product="${product}"
        .collection="${this.wishlistCollection}"
        .viewportObserver="${this.productsViewportObserver}"
        .trackingObserver="${this.productsTrackingObserver}"
        .promotionObserver="${this.productsPromotionObserver}"
        data-test-ents-wishlist-product="${product.syan}"
        ?logged-in="${this.loggedIn}"
        ?is-shared="${this.productsState.isShared() || this.curatedCollectionId}"
        list-count="${countListItems}"
        @aktionsflags:schedule-load=${this.handleScheduleLoadCampFlags}>
      </ents-wishlist-product>
    `;
  }

  private renderWishlistItemSlider(isEmtpyCollection: boolean) {
    if (!this.wishlistCollection || this.curatedCollectionId) {
      return nothing;
    }
    return html`
      <ents-wishlist-item-slider
        .wishlistCollection="${this.wishlistCollection}"
        ?is-empty-collection="${isEmtpyCollection}"
      >
      </ents-wishlist-item-slider>`;
  }

  private renderCollectionHead() {
    const collectionTrackingContext: CollectionTrackingContext = {
      productsAmount: this.products.length,
      eventContext: 'collection_detail',
    };
    return html`
      <div class="ents-wishlist-collection-details">
        <a
          class="bdk-link-secondary"
          size="medium"
          href="${this.curatedCollectionId ? this.wishlistAppConfig.curatedCollectionEndpoint : this.wishlistAppConfig.collectionEndpoint}"
          data-test-ents-wishlist-collection-backlink
        >
          ${hideIconFromAssistiveTechnology(arrowBackIcon())}
          ${this.curatedCollectionId ?
            this.messages['ents_wishlist_curated_collection_details_back_link_text']
            : this.messages['ents_wishlist_collection_details_back_link_text']}
        </a>
        ${this.curator ? html`
          <button
            class="bdk-button-icon"
            variant="just-icon"
            aria-label="${this.messages['ents_wishlist_curated_collection_hint_linktext']} ${this.curator.name}"
            @click="${() => this.curatorLayerState.update({open: true})}">
            <img src="${this.curator.image}" alt="${this.curator.name}" class="ents-curator-image"/>
          </button>
        ` : nothing}
      </div>
      <ents-wishlist-collection-head
        .wishlistCollection="${this.wishlistCollection}"
        .collectionTrackingContext="${collectionTrackingContext}"
        @scroll-top="${this.onScrollTop}"
        ?curated-collection="${this.curatedCollectionId}"
        .curator="${this.curator}"
        with-headline
      >
      </ents-wishlist-collection-head>
    `;
  }

  protected render(): TemplateResult {
    const wishlistRenderCase = WishlistRenderCase.determineRenderCase(
      this.products,
      this.filteredProducts,
      this.wishlistCollection
    );
    const isEmtpyCollection = wishlistRenderCase.productsRenderCase === ProductsRenderCase.RenderCollectionEmptyUnfiltered;
    // empty div around head and products list is needed for the sticky header to stay inside the product grid
    return html`
      ${this.renderEventsIcon()}
      ${this.wishlistMailProductAdded ? this.renderMailProductAdded() : nothing}
      <div>
        ${this.renderCaseHead(wishlistRenderCase)}
        ${this.renderCaseProducts(wishlistRenderCase)}
      </div>
      ${this.renderWishlistItemSlider(isEmtpyCollection)}
    `;
  }

  private renderCaseHead(wishlistRenderCase: WishlistRenderCase) {
    if (wishlistRenderCase.headRenderCase === HeadRenderCase.RenderCollectionHead) {
      return this.renderCollectionHead();
    }
    return nothing;
  }

  private renderCaseProducts(wishlistRenderCase: WishlistRenderCase) {
    switch (wishlistRenderCase.productsRenderCase) {
      case ProductsRenderCase.RenderCollectionEmptyUnfiltered: {
        return this.renderEmptyCollection();
      }
      case ProductsRenderCase.RenderProducts: {
        return this.renderWishlist(wishlistRenderCase.products);
      }
      case ProductsRenderCase.RenderNothing: {
        // the empty view is defined in wishlist.peb and only toggled via CSS class
        // in this component (see "updated" and "toggleEmptyWishlist" functions)
        return nothing;
      }
      case ProductsRenderCase.RenderEmptyFiltered: {
        return this.renderOverfilteredWishlist();
      }
      case ProductsRenderCase.RenderEmptyFilteredExternalNotification: {
        // no products pass the filters, reset them and return all products in wishlist
        this.resetAllFilters();
        return this.renderWishlist(wishlistRenderCase.products);
      }
      default: {
        return this.renderWishlist(wishlistRenderCase.products);
      }
    }
  }

  protected willUpdate(changedProperties: PropertyValues) {
    if (changedProperties.has('products') || changedProperties.has('filterMenu')) {
      this.filteredProducts = this.filterMenu.filterAndSortProducts(this.products, false);
    }
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    if (changedProperties.has('productsState')) {
      this.setProductsForCollection();
      FilterMenu.invalidateCache();
      this.reapplyFilter();
    } else if (changedProperties.has('wishlistCollection')) {
      this.setProductsForCollection();
      FilterMenu.invalidateCache();
    }
  }

  private renderChip(activeOption: OptionUIModel) {
    return html`
      <ents-filter-chip
        .option="${activeOption}"
        .isSort="false">
      </ents-filter-chip>`;
  }

  private handleOverfilteredChange(e: CustomEvent<{ optionId: string, allowRemove: boolean }>) {
    this.onWishlistFilterChangedEvent(e);
    this.onWishlistFilterApply();
  }

  private triggerCampEvent() {
    document.dispatchEvent(new CustomEvent('camp:aktionsflags:initialized'));
  }

  private surveyOpen() {
    this.setSurveyCookie();

    const surveyElement = this.shadowRoot?.querySelector('.ents-inlist-survey') as HTMLElement;
    if (surveyElement) {
      surveyElement.hidden = true;
    }
    document.dispatchEvent(
      new CustomEvent('empera:survey-ssi:open', {detail: {surveyCollectorId: this.wishlistAppConfig.surveyCollectorId}})
    );
  };

  private hideSurvey() {
    this.setSurveyCookie();
    const surveyElement = this.shadowRoot?.querySelector('.ents-inlist-survey') as HTMLElement;
    if (surveyElement) surveyElement.hidden = true;
  }

  private setSurveyCookie() {
    const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `ents_wishlistSurvey=${this.SURVEY_VERSION}; expires=${expires}; path=/`;
  }

}
